import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Lock, X } from 'lucide-react'
import { toast } from 'sonner'
import type { WireArticle } from '../../api/types'
import { useWireAuth } from '../../auth/useWireAuth'
import { useTopics, useRegions, topicLabel, regionLabel } from '../../hooks/useTaxonomy'

interface Props {
  article: WireArticle | null
  onClose: () => void
}

export function ArticleAccessDialog({ article, onClose }: Props) {
  const { user } = useWireAuth()
  const { data: allTopics = [] } = useTopics()
  const { data: allRegions = [] } = useRegions()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [justification, setJustification] = useState('')

  // Items in the article's required sets that the user has no access to
  const missingTopics = article && user
    ? (article.topics ?? []).filter(t => !user.topics.includes(t))
    : []
  const missingRegions = article && user
    ? (article.regions ?? []).filter(r => !user.regions.includes(r))
    : []

  function toggle(slug: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      setSelected(new Set())
      setJustification('')
      onClose()
    }
  }

  function handleRequest() {
    toast.success('Request submitted to JEMS')
    setSelected(new Set())
    setJustification('')
    onClose()
  }

  const hasMissing = missingTopics.length > 0 || missingRegions.length > 0

  return (
    <Dialog.Root open={Boolean(article)} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[460px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="article-access-desc"
        >
          <Dialog.Close
            className="absolute right-3 top-3 rounded p-1 text-fg-secondary transition-colors hover:text-fg"
            aria-label="Close"
          >
            <X size={14} />
          </Dialog.Close>

          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-fill">
              <Lock size={14} className="text-warning-fg" />
            </div>
            <div>
              <Dialog.Title className="text-sm font-semibold text-fg">
                Access Restricted
              </Dialog.Title>
              <Dialog.Description id="article-access-desc" className="mt-1 text-sm text-fg-secondary">
                In order to get access to this article, you must have access to at least one of the
                following:
              </Dialog.Description>
            </div>
          </div>

          {/* Checkbox lists */}
          {hasMissing && (
            <div className="mt-4 space-y-4">
              {missingTopics.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
                    Topics
                  </div>
                  <div className="space-y-0.5">
                    {missingTopics.map(slug => (
                      <label
                        key={slug}
                        className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-fill"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(slug)}
                          onChange={() => toggle(slug)}
                          className="h-3.5 w-3.5 accent-teal-9"
                        />
                        <span className="text-sm text-fg">{topicLabel(allTopics, slug)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {missingRegions.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">
                    Regions
                  </div>
                  <div className="space-y-0.5">
                    {missingRegions.map(slug => (
                      <label
                        key={slug}
                        className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-fill"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(slug)}
                          onChange={() => toggle(slug)}
                          className="h-3.5 w-3.5 accent-teal-9"
                        />
                        <span className="text-sm text-fg">{regionLabel(allRegions, slug)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Justification */}
          <div className="mt-4">
            <label
              htmlFor="access-justification"
              className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-fg-secondary"
            >
              Justification <span className="text-error-fg">*</span>
            </label>
            <textarea
              id="access-justification"
              value={justification}
              onChange={e => setJustification(e.target.value)}
              rows={3}
              placeholder="Briefly describe your need-to-know for this content…"
              className="w-full resize-none rounded border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-secondary/50 focus:border-accent-primary-solid focus:outline-none"
            />
          </div>

          {/* Actions */}
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleRequest}
              disabled={selected.size === 0 || justification.trim().length === 0}
              className="rounded bg-teal-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
            >
              Request from JEMS
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
